import React, { useEffect, useRef, useState } from "react";
import { TransactionsRow } from "../../types/transactions";
import transactionsApi from "../../api/transactioApi/transactionsApi";
import { Funnel } from "lucide-react";

interface Props {
  transactions?: TransactionsRow[]; // optional: if omitted, component will fetch its own data
}

const PAGE_SIZE = 10;
const DEBOUNCE_DELAY = 500;

const TransactionTable: React.FC<Props> = ({
  transactions: propTransactions,
}) => {
  const [internalTransactions, setInternalTransactions] = useState<
    TransactionsRow[]
  >([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(PAGE_SIZE);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isControlled =
    Array.isArray(propTransactions) && propTransactions.length > 0;

  // Search + filters + sort
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const searchTimerRef = useRef<NodeJS.Timeout | null>(null);

  // default date range: current month (1st of month → today)
  const nowIso = new Date().toISOString().split("T")[0];
  const firstOfMonthIso = (() => {
    const d = new Date();
    d.setDate(1);
    return d.toISOString().split("T")[0];
  })();

  // default date range values (inputs show date-only); debouncedFilters default to full datetimes
  const defaultStartDate = firstOfMonthIso;
  const defaultEndDate = nowIso;
  const defaultStartDateTime = `${defaultStartDate}T00:00:00`;
  const defaultEndDateTime = `${defaultEndDate}T23:59:59`;

  const [filters, setFilters] = useState<{ [k: string]: string }>({
    start_date: defaultStartDate,
    end_date: defaultEndDate,
  });
  const [debouncedFilters, setDebouncedFilters] = useState<{
    [k: string]: string;
  }>({ start_date: defaultStartDateTime, end_date: defaultEndDateTime });
  const filterTimerRef = useRef<NodeJS.Timeout | null>(null);
  const suppressDebouncedFetchRef = useRef(false);

  const [sortField, setSortField] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  // debounce search
  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, DEBOUNCE_DELAY);
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, [search]);

  // debounce filters
  useEffect(() => {
    if (filterTimerRef.current) clearTimeout(filterTimerRef.current);
    filterTimerRef.current = setTimeout(() => {
      // Do NOT propagate date inputs automatically — only non-date filters should debounce into debouncedFilters.
      // Preserve any existing start_date/end_date in debouncedFilters until user clicks Apply.
      const applied: { [k: string]: string } = { ...filters };
      const sd = applied.start_date;
      const ed = applied.end_date;
      // remove date-only keys from automatic propagation
      delete applied.start_date;
      delete applied.end_date;

      // only update debouncedFilters when there are non-date filters to apply
      if (Object.keys(applied).length > 0) {
        setDebouncedFilters((prev) => ({ ...prev, ...applied }));
        setPage(1);
      }
    }, DEBOUNCE_DELAY);
    return () => {
      if (filterTimerRef.current) clearTimeout(filterTimerRef.current);
    };
  }, [filters]);

  const sortQuery = () => {
    if (!sortField) return undefined;
    return sortOrder === "desc" ? `-${sortField}` : sortField;
  };

  const fetchPage = async (p = 1, overrides: Record<string, unknown> = {}) => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, unknown> = {
        page: p,
        page_size: pageSize,
      };

      // prefer global search; otherwise use id filter as search when provided
      if (debouncedSearch) params.search = debouncedSearch;
      else if (debouncedFilters.id) params.search = debouncedFilters.id;
      // map to API query param names per spec
      if (debouncedFilters.station_id && debouncedFilters.station_id !== "all")
        params.station_id = debouncedFilters.station_id;
      if (debouncedFilters.status && debouncedFilters.status !== "all")
        params.status_filter = debouncedFilters.status;
      if (debouncedFilters.type && debouncedFilters.type !== "all")
        params.type_filter = debouncedFilters.type;
      if (debouncedFilters.start_date)
        params.start_date = debouncedFilters.start_date;
      if (debouncedFilters.end_date)
        params.end_date = debouncedFilters.end_date;
      const s = sortQuery();
      if (s) params.sort = s;

      // apply any explicit overrides passed (used by Apply button to avoid stale state)
      Object.assign(params, overrides);

      // DEBUG: log outgoing params to help diagnose 422 issues
      // eslint-disable-next-line no-console
      console.debug("transactions.fetch params:", params);

      const res = await transactionsApi.getTransactions(params);

      // DEBUG: log response summary
      // eslint-disable-next-line no-console
      console.debug("transactions.fetch response:", {
        page: res.page,
        total: res.total,
        items: (res.items || []).length,
      });
      setInternalTransactions(res.items as TransactionsRow[]);
      setTotal(res.total);
      setPage(res.page);
    } catch (err: any) {
      console.error("Failed to load transactions:", err);
      setError(err?.message || "Failed to load transactions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (suppressDebouncedFetchRef.current) {
      // skip this automatic fetch because Apply already triggered a request
      suppressDebouncedFetchRef.current = false;
      return;
    }
    if (!isControlled) fetchPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, debouncedFilters, sortField, sortOrder]);

  const rows = isControlled
    ? (propTransactions as TransactionsRow[])
    : internalTransactions;

  const onSort = (field: string) => {
    setPage(1);
    setSortOrder((prev) =>
      sortField === field ? (prev === "asc" ? "desc" : "asc") : "asc",
    );
    setSortField(field);
  };

  const handleFilterChange = (field: string, value: string) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const clearFilter = (field: string) => {
    setFilters((prev) => {
      const copy = { ...prev };
      delete copy[field];
      return copy;
    });
    // For date fields, do not immediately alter debouncedFilters — wait for Apply
    if (field !== "start_date" && field !== "end_date") {
      setDebouncedFilters((prev) => {
        const copy = { ...prev };
        delete copy[field];
        return copy;
      });
    }
    setPage(1);
  };

  const [activeFilterColumn, setActiveFilterColumn] = useState<string | null>(
    null,
  );
  const toggleFilterDropdown = (field: string) => {
    setActiveFilterColumn((prev) => (prev === field ? null : field));
  };

  const SortIcon = ({ field }: { field: string }) => {
    if (sortField !== field) return <span className="sort">⇅</span>;
    return sortOrder === "asc" ? (
      <span className="sort">↑</span>
    ) : (
      <span className="sort">↓</span>
    );
  };

  const FilterIcon = ({ field }: { field: string }) => {
    const isActive = !!filters[field] && filters[field].trim() !== "";
    return (
      <span
        className={`filter-icon ${isActive ? "active" : ""}`}
        onClick={(e) => {
          e.stopPropagation();
          toggleFilterDropdown(field);
        }}
      >
        <Funnel size={14} />
      </span>
    );
  };

  const FilterDropdown = ({ field }: { field: string }) => {
    if (activeFilterColumn !== field) return null;

    // small preset options for status and type
    if (field === "status") {
      const opts = ["all", "completed", "failed", "pending"];
      return (
        <div className="filter-dropdown" onClick={(e) => e.stopPropagation()}>
          <div className="filter-dropdown-content">
            {opts.map((o) => (
              <div
                key={o}
                className={`filter-option ${filters[field] === o ? "selected" : ""}`}
                onClick={() => {
                  handleFilterChange(field, o);
                  setActiveFilterColumn(null);
                  setPage(1);
                }}
              >
                {o === "all" ? "All" : o}
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (field === "type") {
      const opts = ["all", "credited", "debited"];
      return (
        <div className="filter-dropdown" onClick={(e) => e.stopPropagation()}>
          <div className="filter-dropdown-content">
            {opts.map((o) => (
              <div
                key={o}
                className={`filter-option ${filters[field] === o ? "selected" : ""}`}
                onClick={() => {
                  handleFilterChange(field, o);
                  setActiveFilterColumn(null);
                  setPage(1);
                }}
              >
                {o === "all" ? "All Types" : o}
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (field === "date") {
      return (
        <div className="filter-dropdown" onClick={(e) => e.stopPropagation()}>
          <div className="filter-dropdown-content">
            <label>From</label>
            <input
              type="date"
              value={filters.start_date ?? firstOfMonthIso}
              onChange={(e) => handleFilterChange("start_date", e.target.value)}
            />
            <label>To</label>
            <input
              type="date"
              value={filters.end_date ?? nowIso}
              onChange={(e) => handleFilterChange("end_date", e.target.value)}
            />
            <div style={{ marginTop: 8 }}>
              <button
                className="primary-btn"
                onClick={() => {
                  // apply immediately: send full datetimes expected by API (use T separator)
                  const sd = String(filters.start_date || "").split("T")[0];
                  const ed = String(filters.end_date || "").split("T")[0];
                  const startDateTime = sd ? `${sd}T00:00:00` : undefined;
                  const endDateTime = ed ? `${ed}T23:59:59` : undefined;

                  const applied = { ...filters };
                  if (sd) applied.start_date = `${sd}T00:00:00`;
                  if (ed) applied.end_date = `${ed}T23:59:59`;

                  // suppress the subsequent debouncedFilters effect-triggered fetch
                  suppressDebouncedFetchRef.current = true;
                  setDebouncedFilters(applied);
                  setActiveFilterColumn(null);
                  setPage(1);
                  // pass explicit datetime overrides to avoid validation errors and fetch immediately
                  void fetchPage(1, {
                    ...(startDateTime ? { start_date: startDateTime } : {}),
                    ...(endDateTime ? { end_date: endDateTime } : {}),
                  });
                }}
              >
                Apply
              </button>
              <button
                className="secondary-btn"
                onClick={() => {
                  clearFilter("start_date");
                  clearFilter("end_date");
                  setActiveFilterColumn(null);
                }}
              >
                Clear
              </button>
            </div>
          </div>
        </div>
      );
    }

    // default free-text filter
    return (
      <div className="filter-dropdown" onClick={(e) => e.stopPropagation()}>
        <div className="filter-dropdown-content">
          <input
            type="text"
            placeholder={`Filter ${field}...`}
            value={filters[field] || ""}
            onChange={(e) => handleFilterChange(field, e.target.value)}
            autoFocus
          />
          {filters[field] && (
            <button
              className="clear-filter-btn"
              onClick={() => {
                clearFilter(field);
                setActiveFilterColumn(null);
              }}
            >
              Clear
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="transactions-table">
      <div className="transactions-header">
        <h3>All Transactions</h3>

        <div className="transactions-controls">
          <input
            className="search-input"
            placeholder="Search by id, station, customer..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {error && <div className="error">{error}</div>}

      <div className="table-wrapper">
        {loading && (
          <div className="table-loader-overlay">
            <div className="loader" />
          </div>
        )}
        <table>
          <thead>
            <tr>
              <th>
                <div className="th-content">
                  <span className="th-label" onClick={() => onSort("id")}>
                    ID <SortIcon field="id" />
                  </span>
                  <FilterIcon field="id" />
                </div>
                <FilterDropdown field="id" />
              </th>

              <th>
                <div className="th-content">
                  <span className="th-label">Station</span>
                </div>
              </th>

              <th>
                <div className="th-content">
                  <span className="th-label">Customer</span>
                </div>
              </th>

              <th>
                <div className="th-content">
                  <span className="th-label" onClick={() => onSort("amount")}>
                    Amount <SortIcon field="amount" />
                  </span>
                  <FilterIcon field="amount" />
                </div>
                <FilterDropdown field="amount" />
              </th>

              <th>
                <div className="th-content">
                  <span className="th-label">Duration</span>
                </div>
              </th>

              <th>
                <div className="th-content">
                  <span className="th-label">Energy</span>
                </div>
              </th>

              <th>
                <div className="th-content">
                  <span className="th-label" onClick={() => onSort("status")}>
                    Status <SortIcon field="status" />
                  </span>
                  <FilterIcon field="status" />
                </div>
                <FilterDropdown field="status" />
              </th>

              <th>
                <div className="th-content">
                  <span className="th-label" onClick={() => onSort("date")}>
                    Date/Time <SortIcon field="date" />
                  </span>
                  <FilterIcon field="date" />
                </div>
                <FilterDropdown field="date" />
              </th>

              <th>Type</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((tx: TransactionsRow) => (
              <tr key={tx.id}>
                <td>{tx.id}</td>
                <td>{tx.station}</td>
                <td>{tx.customer}</td>
                <td>
                  {tx.amount_display ??
                    (typeof tx.amount === "number"
                      ? `₹${tx.amount.toFixed(2)}`
                      : tx.amount)}
                </td>
                <td>
                  {tx.duration_display ??
                    (tx.duration_min !== undefined
                      ? `${tx.duration_min} min`
                      : "-")}
                </td>
                <td>
                  {tx.energy_display ??
                    (tx.energy_kwh !== undefined
                      ? `${tx.energy_kwh} kWh`
                      : "-")}
                </td>
                <td>
                  <span className={`status ${tx.status}`}>{tx.status}</span>
                </td>
                <td>{tx.date}</td>
                <td>{tx.type}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* end table-wrapper */}

      {/* Pagination controls only when component fetches its own data */}
      {!isControlled && (
        <div className="pagination">
          <button
            onClick={() => fetchPage(Math.max(1, page - 1))}
            disabled={page <= 1}
          >
            Prev
          </button>
          <span>
            Page {page} — {total} items
          </span>
          <button
            onClick={() => fetchPage(page + 1)}
            disabled={page * pageSize >= total}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default TransactionTable;
